import { Company } from '@kanda-libs/ks-frontend-services';

import get from 'lodash.get';

import { ORDER, SORTING } from './constants';

import { CombinedEnrolment } from '../RateRequestListPage/helpers';

export interface SimplifiedCombinedEnrolment {
  id: string;
  companyName: string;
  requester: string;
  rateToAdd: string;
  rateToRemove: string;
  dateRequested: string;
}

const getCompanyName = (company?: Company): string => {
  if (!company) return '--';
  if (company.company_type === 'sole_trader')
    return (
      company?.contact_info?.trading_name ||
      company?.sole_trader_info?.trading_name ||
      '--'
    );
  return (
    company?.contact_info?.trading_name ||
    company?.limited_company_info?.company_name ||
    '--'
  );
};

const parseDate = (date?: Date): string => {
  if (!date) return '--';
  const dateObject = new Date(date);
  const dateStamp = [
    `0${dateObject.getDate()}`.slice(-2),
    `0${dateObject.getMonth() + 1}`.slice(-2),
    date.getFullYear(),
  ].join('/');
  const timeStamp = [
    `0${date.getHours()}`.slice(-2),
    `0${date.getMinutes()}`.slice(-2),
    `0${date.getSeconds()}`.slice(-2),
  ].join(':');
  return [dateStamp, timeStamp].join(' ');
};

export const simplifyCombinedEnrolment = (
  combined: CombinedEnrolment[],
): SimplifiedCombinedEnrolment[] =>
  combined.map((val) => ({
    id: val.id,
    companyName: getCompanyName(val.company),
    requester: val.rate_request.requester,
    rateToAdd: val.rate_request.to_add.length === 0 ? 'no' : 'yes',
    rateToRemove: val.rate_request.to_remove.length === 0 ? 'no' : 'yes',
    dateRequested: parseDate(val.rate_request.metadata?.updated_at),
  }));

export const formatDate = (date: string): string => {
  if (date === '') return new Date(0).toISOString();
  const [datestamp, timestamp] = date.split(' ');
  return [datestamp.split('/').reverse().join('-'), timestamp].join('T');
};

export const sortByStringDate = <T>(
  data: T[],
  key: string,
  order: string,
): T[] =>
  data.sort((datum1: T, datum2: T) => {
    const date1 = formatDate(get(datum1, key) || '');
    const date2 = formatDate(get(datum2, key) || '');
    const parsed1 = new Date(date1);
    const parsed2 = new Date(date2);
    const diff = parsed2.getTime() - parsed1.getTime();
    return order === ORDER.NORMAL ? diff : -1 * diff;
  });

export const sortByString = <T>(data: T[], key: string, order: string): T[] =>
  data.sort((datum1: T, datum2: T) => {
    const string1 = get(datum1, key) || '';
    const string2 = get(datum2, key) || '';
    if (order === ORDER.NORMAL) return string1.localeCompare(string2);
    return string2.localeCompare(string1);
  });

export const getYesNoValue = (val: string, order: string): number => {
  if (val === 'yes') {
    if (order === ORDER.NORMAL) return -1;
    return 1;
  }
  if (val === 'no') {
    if (order === ORDER.NORMAL) return 1;
    return -1;
  }
  return 0;
};

export const sortByYesNo = <T>(data: T[], key: string, order: string): T[] =>
  data.sort((datum1: T, datum2: T) => {
    const val1 = getYesNoValue(get(datum1, key) || '', order);
    const val2 = getYesNoValue(get(datum2, key) || '', order);
    return val1 - val2;
  });

export const sortData = <T>(
  data: T[],
  sorting: string,
  order: string,
): T[] | undefined => {
  if (!data) return undefined;
  if (data.length === 0) return [];
  if (sorting === SORTING.DATE_REQUESTED)
    return sortByStringDate(data, 'dateRequested', order);
  if (sorting === SORTING.ID) return sortByString(data, 'id', order);
  if (sorting === SORTING.COMPANY_NAME)
    return sortByString(data, 'companyName', order);
  if (sorting === SORTING.RATE_ADD)
    return sortByYesNo(data, 'rateToAdd', order);
  if (sorting === SORTING.RATE_REMOVE)
    return sortByYesNo(data, 'rateToRemove', order);
  return data;
};
